import { FC, useRef, useState } from 'react';
import { CellData } from '../Calendar/DayCell/DayCell';
import useClinicDateOff from 'hooks/useClinicDateOff';
import moment from 'moment';
import { DEFAULT_WORKING_HOUR, NEXT_23_MONTH } from 'utils/constants';
import { IService } from 'interfaces/services';
import DefaultModal from 'components/Modals/DefaultModal';
import SelectType from 'components/SelectType/SelectType';
import CommonButton from 'components/CommonButton/CommonButton';
import CustomDatePicker from 'components/CustomDatePicker/CustomDatePicker';
import DateRange, { IDateRange } from 'components/DateRange/DateRange';
import ApplyOption from 'components/ApplyOption/ApplyOption';
import SelectTime from 'pages/OfficeSchedulePage/DayTimeSlot/SelectTime/SelectTime';
import OperatoryDropdownWrapper from '../AdvancedSetting/RegularWorkingHour/OperatoryDropdownWrapper/OperatoryDropdownWrapper';
import { ReactComponent as ClearIcon } from 'assets/icons/clear.svg';
import { useForm } from 'react-hook-form';
import { TimeSlots } from '../AdvancedSetting/AdvancedSetting';
import axiosInstance from 'apis/axiosInstance';
import { renderToast } from 'components/Toast/Toast';
import useMatchMutate from 'hooks/useMatchMutate';
import useModal from 'hooks/useModal';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import useServiceSyncException from 'hooks/useServiceSyncException';
import useServiceSchedule from 'hooks/useServiceSchedule';
import { ServiceSchedule } from 'interfaces/serviceSchedule';
import ServiceScheduleException from 'interfaces/serviceScheduleException';
import OverrideModal, { OverwriteOption } from './OverrideModal/OverrideModal';

interface EditScheduleModalProps {
  selectedCellData: Pick<
    CellData,
    'date' | 'day' | 'shifts' | 'isServiceClosed'
  >;
  service: IService;
  onClose: () => void;
  isCreatedMode?: boolean;
}

const options = [
  { label: 'Available', isWorkingDay: true },
  { label: 'Unavailable', isWorkingDay: false },
];

const EditScheduleModal: FC<EditScheduleModalProps> = ({
  service,
  selectedCellData,
  onClose,
  isCreatedMode,
}) => {
  const url = `/services/${service.id}/exceptions`;

  const { data: clinicDateOff, isLoading: isClinicDateOffLoading } =
    useClinicDateOff({
      startDate: moment().format('YYYY-MM-DD'),
      endDate: moment().add(NEXT_23_MONTH, 'months').format('YYYY-MM-DD'),
      isClosed: true,
    });

  const {
    data: serviceSyncException,
    isLoading: isServiceSyncExceptionLoading,
  } = useServiceSyncException({
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().add(NEXT_23_MONTH, 'months').format('YYYY-MM-DD'),
    serviceId: service.id,
  });

  const { data: serviceSchedule, isLoading: isServiceScheduleLoading } =
    useServiceSchedule(service.id);

  const isLoading =
    isClinicDateOffLoading ||
    isServiceSyncExceptionLoading ||
    isServiceScheduleLoading;

  const matchMutate = useMatchMutate();

  const [isSingleDay, setIsSingleDay] = useState(true);

  const [selectedType, setSelectedType] = useState(() =>
    selectedCellData.isServiceClosed ? options[1] : options[0]
  );

  const [selectedSingleDate, setSelectedSingleDate] = useState(() =>
    moment(selectedCellData.date).toDate()
  );

  const [dateRange, setDateRange] = useState<IDateRange>(() => ({
    dateFrom: moment(selectedCellData.date).toDate(),
    dateTo: null,
  }));

  const [isMultipleDayChecking, setIsMultipleDayChecking] = useState(false);

  const [exceptionDates, setExceptionDates] = useState<
    ServiceScheduleException[] | null
  >(null);

  const dateRangePickerRef = useRef<HTMLButtonElement | null>(null);

  const { setIsAllowed } = useModal({
    onClose,
    isLoading: isLoading,
  });

  const { watch, setValue, handleSubmit, formState } = useForm<{
    timeSlots: TimeSlots[];
  }>({
    defaultValues: {
      timeSlots: selectedCellData.shifts.every(
        (shift) => shift.startTime && shift.endTime
      )
        ? selectedCellData.shifts.map((shift) => ({
            start: shift.startTime,
            end: shift.endTime,
            operatories: shift.operatories.map((operatory) => ({
              label: operatory.name,
              value: operatory.id,
            })),
          }))
        : [],
    },
  });

  const timeSlots = watch('timeSlots');

  const getExcludeDates = () => {
    if (!clinicDateOff || !serviceSyncException) return [];
    const clinicDateOffDate = clinicDateOff.map((d) => moment(d.date).toDate());
    const serviceExceptionDate = serviceSyncException.map((d) =>
      moment(d).toDate()
    );
    const allDates = [...clinicDateOffDate, ...serviceExceptionDate];

    const uniqueDates = Array.from(
      new Set(allDates.map((date) => date.getTime()))
    )
      .map((time) => new Date(time))
      .sort((a, b) => a.getTime() - b.getTime());

    return uniqueDates;
  };

  const onTimeChange = (
    _: string,
    value: string,
    type: string,
    index: number
  ) => {
    const updatedTimeSlots = timeSlots.map((slot, i) =>
      i === index ? { ...slot, [type]: value } : slot
    );

    setValue('timeSlots', updatedTimeSlots, { shouldValidate: true });
  };

  const onAddTimeSlot = () => {
    const newTimeSlot = {
      start: DEFAULT_WORKING_HOUR.START,
      end: DEFAULT_WORKING_HOUR.END,
      operatories: [],
    };

    const updatedTimeSlots = [...timeSlots, newTimeSlot];

    setValue('timeSlots', updatedTimeSlots, { shouldValidate: true });

    if (updatedTimeSlots.length === 1) {
      setSelectedType(options[0]);
    }
  };

  const onDeleteTimeSlot = (index: number) => {
    const updatedTimeSlots = timeSlots.filter((_, i) => i !== index);

    if (updatedTimeSlots.length === 0) {
      setSelectedType(options[1]);
    }

    setValue('timeSlots', updatedTimeSlots, { shouldValidate: true });
  };

  const onOperatoryUpdated = (newValue: any[], index: number) => {
    const updatedTimeSlots = timeSlots.map((slot, i) =>
      i === index ? { ...slot, operatories: newValue } : slot
    );

    setValue('timeSlots', updatedTimeSlots, { shouldValidate: true });
  };

  const getRangeExceptionDate = async (timeRange: string[]) => {
    const query = new URLSearchParams({
      startDate: timeRange[0],
      endDate: timeRange[1],
    }).toString();

    try {
      const response = await axiosInstance.get(
        `/services/${service.id}/custom-days?${query}`
      );
      return response.data;
    } catch (error) {}
  };

  const updateMultipleTimeSlots = async ({
    timeSlots,
  }: {
    timeSlots: TimeSlots[];
  }) => {
    const timeRange = [
      moment(dateRange.dateFrom).format('YYYY-MM-DD'),
      moment(dateRange.dateTo).format('YYYY-MM-DD'),
    ];

    setIsMultipleDayChecking(true);

    const exceptionDates = await getRangeExceptionDate(timeRange);

    setIsMultipleDayChecking(false);
    if (exceptionDates && exceptionDates.length > 0) {
      setExceptionDates(exceptionDates);
      return;
    }

    let listOfDate: string[] = [];
    const excludeDates = getExcludeDates().map((date) =>
      moment(date).format('YYYY-MM-DD')
    );

    for (
      const date = moment(timeRange[0]);
      date.isSameOrBefore(timeRange[1]);
      date.add(1, 'days')
    ) {
      listOfDate.push(date.format('YYYY-MM-DD'));
    }

    listOfDate = listOfDate.filter((date) => !excludeDates.includes(date));

    const payload = listOfDate.map((date) => ({
      date,
      isAvailable: selectedType.isWorkingDay,
      ...(selectedType.isWorkingDay && {
        shifts: timeSlots.map((timeSlot) => ({
          startTime: timeSlot.start,
          endTime: timeSlot.end,
          operatoryIds: timeSlot.operatories.map(
            (operatory) => operatory.value
          ),
        })),
      }),
    }));

    try {
      await axiosInstance.post(url, payload);
      renderToast({
        type: 'success',
        message: 'Service schedule updated successfully',
      });
      await matchMutate(/\/services\/[\s\S]+\/exceptions[\s\S]+/, {
        revalidate: true,
      });
      await matchMutate(/\/services\/[\s\S]+\/custom-days[\s\S]+/, {
        revalidate: true,
      });
      onClose();
    } catch (error) {
      renderToast({
        type: 'error',
        message: 'Failed to update service schedule. Please try again.',
      });
    }
  };

  const checkIsSameAsRegularWorkingHour = (
    timeSlots: TimeSlots[],
    regularWorkingHour: ServiceSchedule[]
  ) => {
    if (timeSlots.length !== regularWorkingHour.length) return false;

    return timeSlots.every((timeSlot) =>
      regularWorkingHour.some(
        (workingHour) =>
          workingHour.startTime === timeSlot.start &&
          workingHour.endTime === timeSlot.end &&
          workingHour.operatories.length === timeSlot.operatories.length &&
          workingHour.operatories.every((operatory) =>
            timeSlot.operatories.some((op) => op.value === operatory.id)
          )
      )
    );
  };

  const onSubmit = async ({ timeSlots }: { timeSlots: TimeSlots[] }) => {
    if (!serviceSchedule) return;
    setIsAllowed(false);
    if (!isSingleDay) {
      await updateMultipleTimeSlots({ timeSlots });
      return;
    }

    const payload = [
      {
        date: moment(selectedSingleDate).format('YYYY-MM-DD'),
        isAvailable: selectedType.isWorkingDay,
        ...(selectedType.isWorkingDay && {
          shifts: timeSlots.map((timeSlot) => ({
            startTime: timeSlot.start,
            endTime: timeSlot.end,
            operatoryIds: timeSlot.operatories.map(
              (operatory) => operatory.value
            ),
          })),
        }),
      },
    ];

    const day = moment(selectedSingleDate)
      .format('ddd')
      .toLowerCase() as keyof typeof serviceSchedule;

    const regularWorkingHourCurrentDate = serviceSchedule[day];

    const isSameAsRegularWorkingHour = checkIsSameAsRegularWorkingHour(
      timeSlots,
      regularWorkingHourCurrentDate
    );

    if (isSameAsRegularWorkingHour) {
      const payload = {
        dates: [moment(selectedSingleDate).format('YYYY-MM-DD')],
      };
      try {
        await axiosInstance.delete(url, { data: payload });
        renderToast({
          type: 'success',
          message: 'Service schedule updated successfully',
        });
        await matchMutate(/\/services\/[\s\S]+\/exceptions[\s\S]+/, {
          revalidate: true,
        });
        await matchMutate(/\/services\/[\s\S]+\/custom-days[\s\S]+/, {
          revalidate: true,
        });
        onClose();
      } catch (error) {
        renderToast({
          type: 'error',
          message: 'Failed to update service schedule. Please try again.',
        });
      }
      return;
    }

    try {
      await axiosInstance.post(url, payload);
      renderToast({
        type: 'success',
        message: 'Service schedule updated successfully',
      });
      await matchMutate(/\/services\/[\s\S]+\/exceptions[\s\S]+/, {
        revalidate: true,
      });
      await matchMutate(/\/services\/[\s\S]+\/custom-days[\s\S]+/, {
        revalidate: true,
      });
      onClose();
    } catch (error) {
      renderToast({
        type: 'error',
        message: 'Failed to update service schedule. Please try again.',
      });
    }
  };

  const isDataValid = timeSlots.every(
    (timeSlot) =>
      (!timeSlot.start && !timeSlot.end) ||
      (timeSlot.start && timeSlot.end && timeSlot.operatories.length > 0)
  );

  const onOverwrite = async (options: OverwriteOption[]) => {
    if (!serviceSchedule) return;
    const timeRange = [
      moment(dateRange.dateFrom).format('YYYY-MM-DD'),
      moment(dateRange.dateTo).format('YYYY-MM-DD'),
    ];

    const excludedDates = getExcludeDates().map((date) =>
      moment(date).format('YYYY-MM-DD')
    );

    const postDates = [];
    const deleteDates = [];

    const unCheckedOptions = options.filter((option) => !option.checked);

    for (
      const date = moment(timeRange[0]);
      date.isSameOrBefore(timeRange[1]);
      date.add(1, 'days')
    ) {
      const formattedDate = date.format('YYYY-MM-DD');

      const day = moment(formattedDate)
        .format('ddd')
        .toLowerCase() as keyof typeof serviceSchedule;

      const regularWorkingHourCurrentDate = serviceSchedule[day];

      const isUnchecked = unCheckedOptions.some(
        (option) => option.date === formattedDate
      );

      if (excludedDates.includes(formattedDate) || isUnchecked) {
        continue;
      }

      const isSameAsRegularWorkingHour = checkIsSameAsRegularWorkingHour(
        timeSlots,
        regularWorkingHourCurrentDate
      );

      if (isSameAsRegularWorkingHour) {
        deleteDates.push(formattedDate);
        continue;
      }

      postDates.push(formattedDate);
    }

    const promises = [];

    if (postDates.length > 0) {
      const payload = postDates.map((date) => ({
        date,
        isAvailable: selectedType.isWorkingDay,
        ...(selectedType.isWorkingDay && {
          shifts: timeSlots.map((timeSlot) => ({
            startTime: timeSlot.start,
            endTime: timeSlot.end,
            operatoryIds: timeSlot.operatories.map(
              (operatory) => operatory.value
            ),
          })),
        }),
      }));
      promises.push(axiosInstance.post(url, payload));
    }

    if (deleteDates.length > 0) {
      const payload = {
        dates: deleteDates.map((date) => date),
      };
      promises.push(axiosInstance.delete(url, { data: payload }));
    }

    try {
      await Promise.all(promises);
      renderToast({
        type: 'success',
        message: 'Service schedule updated successfully',
      });
      await matchMutate(/\/services\/[\s\S]+\/exceptions[\s\S]+/, {
        revalidate: true,
      });
      await matchMutate(/\/services\/[\s\S]+\/custom-days[\s\S]+/, {
        revalidate: true,
      });
      onClose();
    } catch (error) {
      renderToast({
        type: 'error',
        message: 'Something went wrong. Please try again later',
      });
    }
  };

  if (isClinicDateOffLoading) {
    return <LoadingScreen />;
  }

  return exceptionDates ? (
    <OverrideModal
      exceptionsDates={exceptionDates}
      onClose={() => {
        setExceptionDates(null);
      }}
      onOverwrite={onOverwrite}
    />
  ) : (
    <DefaultModal>
      <div className="w-[487px] min-h-[290px] max-h-[calc(100vh-3rem)] pl-3.2 pr-4.4 pt-2.4 pb-3.2 text-14 leading-[2.1rem] text-darkest-grey">
        <h3 className="text-16 leading-[2.4rem] font-bold">
          {isCreatedMode ? 'Add custom day(s)' : 'Edit service schedule'}
        </h3>
        <div className="flex items-center mt-1.5">
          <span className="text-13 leading-[2rem]">Type</span>
          <div className="flex-1 w-full ml-8">
            <SelectType
              selectedType={selectedType}
              setSelectedType={(value) => {
                const selectedOption = options.find(
                  (option) => option === value
                )!;
                const updatedTimeSlots = selectedOption.isWorkingDay
                  ? [
                      {
                        start: DEFAULT_WORKING_HOUR.START,
                        end: DEFAULT_WORKING_HOUR.END,
                        operatories: [],
                      },
                    ]
                  : [];
                setValue('timeSlots', updatedTimeSlots, {
                  shouldValidate: true,
                });
                setSelectedType(value);
              }}
              options={options}
            />
          </div>
        </div>

        <div className="flex justify-between">
          <span className="self-center">Hours</span>
          <div className="flex flex-1 max-w-[28rem] flex-col gap-y-2 mt-1.4 overflow-y-auto scrollbar max-h-[60vh]">
            {timeSlots.map((timeSlot, index) => (
              <div key={index} className="flex gap-x-0.8">
                <div className="border border-[#F5F5F5] flex-1 bg-[#fcfcfc] min-h-[15.6rem] rounded-[1.2rem] p-1.6">
                  <div className="flex gap-x-1.6 items-center">
                    <div className="flex flex-1 items-center">
                      <div className="flex-1 relative">
                        <SelectTime
                          type={'start'}
                          day={selectedCellData.day}
                          value={timeSlot.start!}
                          end={timeSlot.end!}
                          onChange={(...params) => {
                            onTimeChange(...params, index);
                          }}
                          buttonClassName="!w-full !h-4"
                          dropdownClassName="!w-full items-center h-15"
                        />
                      </div>

                      <span className="mx-0.8">-</span>

                      <div className="flex-1 relative">
                        <SelectTime
                          type={'end'}
                          day={selectedCellData.day}
                          value={timeSlot.end!}
                          start={timeSlot.start!}
                          onChange={(...params) => {
                            onTimeChange(...params, index);
                          }}
                          buttonClassName="!w-full !h-4"
                          dropdownClassName="!w-full items-center h-15"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-1.6">
                    <p className="mb-0.8">Operatories</p>
                    <OperatoryDropdownWrapper
                      onUpdate={(newValue) =>
                        onOperatoryUpdated(newValue, index)
                      }
                      value={timeSlot.operatories}
                      selectProps={{
                        menuPortalTarget: document.body,
                      }}
                    />
                  </div>
                </div>
                <button
                  className="self-start"
                  onClick={() => onDeleteTimeSlot(index)}
                >
                  <ClearIcon className="w-1.6 h-1.6" />
                </button>
              </div>
            ))}

            <button
              className="text-magenta font-bold mt-1.2"
              onClick={onAddTimeSlot}
            >
              + Add another
            </button>
          </div>
        </div>

        <div className="mt-2.9">
          <div className="flex items-center">
            <span className="text-13 leading-[2rem]">Apply to</span>
            <div className="flex-1 ml-4.5">
              <ApplyOption
                isSingleDay={isSingleDay}
                setIsSingleDay={setIsSingleDay}
                dateTo={dateRange.dateTo}
                dateRangePickerRef={dateRangePickerRef}
              />
            </div>
          </div>
        </div>

        <div className="mt-1.9 flex">
          {isSingleDay && (
            <CustomDatePicker
              excludeDates={getExcludeDates()}
              selected={selectedSingleDate}
              onChange={(date: Date | null) => {
                if (date) {
                  setSelectedSingleDate(date);
                }
              }}
              minDate={new Date()}
              maxDate={moment().add(NEXT_23_MONTH, 'months').toDate()}
              customInput={
                <div className="flex items-center gap-x-7.8">
                  <span className="text-13 leading-[2rem]">Date</span>
                  <button
                    className={`w-full h-3 cursor-pointer rounded-[1rem] bg-white border-[0.5px] border-light-grey text-left shadow-primary hover:border-magenta hover:shadow-input-active px-1.6 ml-2.4`}
                  >
                    {moment(selectedSingleDate).format('MMMM DD, YYYY')}
                  </button>
                </div>
              }
              popperModifiers={[
                {
                  name: 'offset',
                  options: {
                    offset: [116, -2],
                  },
                },
              ]}
            />
          )}
          <div className={isSingleDay ? 'hidden' : 'flex even:text-red'}>
            <DateRange
              dateRange={dateRange}
              setDateRange={setDateRange}
              dateRangePickerRef={dateRangePickerRef}
              excludeDates={getExcludeDates()}
              popoverProps={{
                classNames: { dropdown: '' },
              }}
            />
          </div>
        </div>

        <div className="mt-2.8 flex gap-x-1.6 justify-end">
          <CommonButton
            variant="secondary"
            className="!min-w-[13rem] !min-h-[3.2rem]"
            onClick={onClose}
          >
            Cancel
          </CommonButton>
          <CommonButton
            className="!min-w-[13rem] !min-h-[3.2rem]"
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting || isMultipleDayChecking}
            disabled={!isDataValid}
          >
            Confirm
          </CommonButton>
        </div>
      </div>
    </DefaultModal>
  );
};

export default EditScheduleModal;
